import { toNumber } from './helpers.js';
import { STYLE_KEYS } from './keys.js';

const RegExps = {
    NUMERIC: /.*[0-9]+.*$/,
    ALPHA: /.*[a-zA-Z]+.*$/,
    PASSWORD: /^(?=.*\d)[\w`!@#$%^&*()-_=+[\]{};:"|\\,.]+$/,
    COLOR_HEX: /^#([0-9a-fA-F]{3}){1,2}$/, // валидно #abc/#ABC/#abcdef/#ABCDEF
    COLOR_ALPHA_HEX: /^#([0-9a-fA-F]{3,4}){1,2}$/, // валидно #abc/#ABC/#abcd/#ABCD/#abcdef/#ABCDEF/#abcdef00/#ABCDEF00
};

/**
 * Возвращает `true`, если передано непустое значение:
 *  - для значений `undefined` и `null` вернет `false`
 *  - для любой валидной даты вернет `true`
 *  - для массива ненулевой длины вернет `true`
 *  - для объекта как минимум с 1 ключом вернет `true`
 *  - в остальных случаях значение приводится к строке,
 *    удаляются концевые пробелы и возвращается `true`
 *    для ненулевого количества символов
 * @param {*} value
 * @returns boolean
 */
export const required = value => {
    if (typeof value === 'undefined' || value === null) {
        return false;
    } else if (typeof value === 'boolean') {
        return value;
    } else if (value instanceof Date) {
        return !isNaN(value.getTime());
    } else if (Array.isArray(value)) {
        return Boolean(value.length);
    } else if (typeof value === 'object') {
        return Boolean(Object.keys(value).length);
    }

    return Boolean(String(value).trim().length);
};

/**
 * Возвращает длину переданного значения:
 *  - для массива - количество элементов
 *  - для объекта - количество ключей
 *  - в остальных случаях значение приводится к строке,
 *    удаляются концевые пробелы и возвращается количество символов
 * @param {*} value
 * @returns number
 */
export const getLength = value => {
    if (typeof value === 'undefined' || value === null) {
        return 0;
    } else if (Array.isArray(value)) {
        return value.length;
    } else if (typeof value === 'object') {
        return Object.keys(value).length;
    }

    return String(value).trim().length;
};

/**
 * Проверяет переданное значение на соответствие заданной минимальной длине
 * @param {number} length
 * @param {*} value
 * @returns boolean
 */
export const minLength = length => value => required(value) && getLength(value) >= length;

/**
 * Проверяет переданное значение на соответствие заданной максимальной длине
 * @param {number} length
 * @param {*} value
 * @returns boolean
 */
export const maxLength = length => value => required(value) && getLength(value) <= length;

/**
 * Проверяет два переданных значения на строгое равенство
 * @param {*} targetValue
 * @param {*} value
 * @returns boolean
 */
export const sameAs = targetValue => value => value === targetValue;

/**
 * Проверяет переданное значение на соответствие заданному регулярному выражению
 * @param {RegExp} expr
 * @param {*} value
 * @returns boolean
 */
export const regex = expr => value => required(value) && expr.test(value);

/**
 * Проверяет является ли переданное значение числом
 * @param {*} value
 * @returns boolean
 */
export const numeric = value => regex(RegExps.NUMERIC)(value);

/**
 * Проверяет является ли переданное значение буквой латинского алфавита
 * @param {*} value
 * @returns boolean
 */
export const alpha = value => regex(RegExps.ALPHA)(value);

/**
 * Проверяет переданное значение на соответствие регулярному выражению пароля
 * @param {*} value
 * @returns boolean
 */
export const password = value => regex(RegExps.PASSWORD)(value);

/**
 * Проверяет переданное значение на соответствие регулярному выражению цвета в формате HEX
 * @param {*} value
 * @returns boolean
 */
export const colorHEX = value => regex(RegExps.COLOR_HEX)(value);

/**
 * Проверяет переданное значение на соответствие регулярному выражению цвета с прозрачностью в формате HEX
 * @param {*} value
 * @returns boolean
 */
export const colorAlphaHEX = value => regex(RegExps.COLOR_ALPHA_HEX)(value);

/**
 * Проверяет можно ли привести значение к числу
 * @param {*} value
 * @returns boolean
 */
export const isValueConvertedToNumber = value => !isNaN(toNumber(value));

/**
 * Проверяет корректность переданной позиции
 * Валидные варианты:
 *  - top/top-start/top-end
 *  - bottom/bottom-start/bottom-end
 *  - left/left-start/left-end
 *  - right/right-start/right-end
 * @param {string} value
 * @returns boolean
 */
export const checkPositionValue = value => /^(top|bottom|left|right)(-start|-end)?$/.test(value.trim());

/**
 * Проверяет корректность переданного класса анимации
 * Валидные варианты:
 *  - fade/fade-up/fade-down/fade-left/fade-right
 * @param {string} value
 * @returns boolean
 */
export const checkTransitionName = value => /^fade(-up|-down|-left|-right)?$/.test(value.trim());

/**
 * Проверяет наличие переданного значения в масиве.
 * По-умолчанию отсутствие переданного значения вернет true
 * @param {array} array
 * @param {*} value
 * @returns boolean
 */
export const isArrayContainsValue = (array, strict = false) => value => {
    let result = array.includes(value);

    if (!strict) result = !required(value) || result;

    return result;
};

/**
 * Проверяет нажатие на одну из специальных клавиш:
 *  - `alt` – `altKey`
 *  - `control` – `ctrlKey`
 *  - `shift` – `shiftKey`
 *  - `window`/`command` – `metaKey`
 * @param {object} e
 * @returns boolean
 */
export const isSpecialKeyPressed = (e = {}) => {
    if (e.metaKey || e.ctrlKey) return true;

    return false;
};


export const isStyleKeyPressed = (e = {}) => {
    const isStyleKey = isSpecialKeyPressed(e);

    if (!isStyleKey) return false;

    return STYLE_KEYS.includes(e.code);
};
